// @ts-ignore
import React, { useState, useEffect, Fragment, useRef } from "react";
import { X, Microscope, MapPin, FlaskConical, Shield } from 'lucide-react';
// @ts-ignore
import moment from "moment";

interface StrainData {
  sysStrainId: number;
  strainId: string;
  sample_id: string;
  centerName: string;
  surveyName: string;
  subcategoryName: string;
  propertyName: string;
  serotype: string;
  sampleCategoryName: string;
  sampleSubcategoryName: string;
  superCategoryName: string;
  antibioticSensitive: string;
  antibioticIntermediate: string;
  antibioticResistant: string;
  virulenceGene: string;
  updatedDate: string;
  testDate: string;
  stateName: string;
  districtName: string;
}

interface StrainDataModalProps {
  data: StrainData;
  onClose: () => void;
}

const StrainDataModal: React.FC<StrainDataModalProps> = ({ data, onClose }) => {
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    backdropFilter: 'blur(4px)',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #0066cc 0%, #004c99 100%)',
    color: '#ffffff',
    padding: '24px 32px',
    borderRadius: '16px 16px 0 0',
    position: 'relative',
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: '24px',
    right: '24px',
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '8px',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.3s ease',
  };

  const contentStyle: React.CSSProperties = {
    padding: '32px',
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    border: '1px solid #e0e5eb',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '18px',
    fontWeight: '600',
    color: '#0066cc',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #0066cc',
  };

  const fieldGroupStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '12px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: '600',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    display: 'block',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: '15px',
    color: '#212529',
    lineHeight: '1.6',
    fontWeight: '600',
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  };

  const getTagStyle = (type: 'sensitive' | 'resistant' | 'intermediate' | 'gene'): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '500',
      display: 'inline-block',
    };

    switch (type) {
      case 'sensitive':
        return { ...baseStyle, backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' };
      case 'resistant':
        return { ...baseStyle, backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' };
      case 'intermediate':
        return { ...baseStyle, backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba' };
      case 'gene':
        return { ...baseStyle, backgroundColor: '#e7f3ff', color: '#004085', border: '1px solid #b8daff' };
    }
  };

  const splitAndTrim = (str: string): string[] => {
    return str.split(',').map(item => item.trim()).filter(item => item.length > 0);
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <button
            style={closeButtonStyle}
            onClick={onClose}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={20} color="#ffffff" />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Microscope size={32} />
            <div>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Strain Data Report</h2>
              {/*<p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>*/}
              {/*  ICMR ID: {data.strainId}*/}
              {/*</p>*/}
            </div>
          </div>
        </div>

        <div style={contentStyle}>
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <FlaskConical size={22} />
              <span>Sample Identification</span>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Strain ID</label>
                <div style={valueStyle}>{data.strainId}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sample ID</label>
                <div style={valueStyle}>{data.sample_id}</div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <MapPin size={22} />
              <span>Location & Sample Information</span>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sample Category</label>
                <div style={valueStyle}>{data.sampleCategoryName}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Sample Subcategory</label>
                <div style={valueStyle}>{data.sampleSubcategoryName}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Isolation Year</label>
                <div style={valueStyle}>{moment(data.testDate).format("YYYY")}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>State</label>
                <div style={valueStyle}>{data.stateName}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>District</label>
                <div style={valueStyle}>{data.districtName}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Center Name</label>
                <div style={valueStyle}>{data.centerName}</div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <Microscope size={22} />
              <span>Pathogen Information</span>
            </div>
            <div style={fieldGroupStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Identified Pathogen</label>
                <div style={{
                  ...valueStyle,
                  fontStyle: "italic",
                }}>{data.subcategoryName} {data.propertyName === "Positive" ? "" : `(${data.propertyName})`}</div>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Serotype</label>
                <div style={valueStyle}>{data.serotype}</div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>
              <Shield size={22} />
              <span>Antibiotic Susceptibility & Virulence Profile</span>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Antibiotic Sensitive</label>
              <div style={tagContainerStyle}>
                {splitAndTrim(data.antibioticSensitive).map((antibiotic, index) => (
                  <span key={index} style={getTagStyle('sensitive')}>
                    {antibiotic}
                  </span>
                ))}
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Antibiotic Resistant</label>
              <div style={tagContainerStyle}>
                {splitAndTrim(data.antibioticResistant).map((antibiotic, index) => (
                  <span key={index} style={getTagStyle('resistant')}>
                    {antibiotic}
                  </span>
                ))}
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Antibiotic Intermediate</label>
              <div style={tagContainerStyle}>
                {splitAndTrim(data.antibioticIntermediate).map((antibiotic, index) => (
                  <span key={index} style={getTagStyle('intermediate')}>
                    {antibiotic}
                  </span>
                ))}
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Virulence Genes</label>
              <div style={tagContainerStyle}>
                {splitAndTrim(data.virulenceGene).map((gene, index) => (
                  <span key={index} style={getTagStyle('gene')}>
                    {gene}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrainDataModal;
